import { Injectable, inject } from "@angular/core";
import { DOCUMENT } from "@angular/common";

import { CountryCodeEnum } from "../models/country.model";
import {
  LANDING_CAMPAIGN_SEGMENT_INDEX,
  LANDING_COUNTRY_SEGMENT_INDEX,
  LANDING_DEFAULT_COUNTRY,
  LANDING_ROUTE_PATH,
  LandingRouteContext,
  SUPPORTED_COUNTRIES,
} from "../models/landing.model";

/**
 * Resolves landing route context (entry type + country + canonical path) from the current URL.
 * It normalizes and validates country values, applies a safe fallback (`TH`), and
 * returns whether the current URL already matches the canonical landing path.
 *
 * This keeps country parsing and canonical-path rules in one place so app initialization,
 * routing, analytics, and i18n all use consistent landing-country state.
 */
@Injectable({ providedIn: "root" })
export class LandingRouteService {
  private readonly document: Document;

  constructor(...args: unknown[]);
  constructor() {
    const [document = inject<Document>(DOCUMENT)] = arguments as unknown as [
      Document | undefined,
    ];

    this.document = document;
  }

  /**
   * Builds landing route context for the current URL.
   *
   * Rules:
   * - Only applies to landing routes.
   * - Country resolution priority: path -> default TH.
   * - Country values are normalized/validated against supported enum values.
   *
   * @returns Landing route context if current URL is a landing route; otherwise `null`.
   */
  getRouteContext(): LandingRouteContext | null {
    const pathname = this.getCurrentPathname();
    if (!this.isLandingRoute(pathname)) {
      return null;
    }

    return this.getRouteContextFromPath(pathname);
  }

  /**
   * Builds landing route context from a pathname.
   *
   * @param pathname Browser pathname to parse.
   * @returns Landing route context for landing paths; otherwise `null`.
   */
  getRouteContextFromPath(pathname: string): LandingRouteContext | null {
    if (!this.isLandingRoute(pathname)) {
      return null;
    }

    const countryFromPath = this.getCountryFromPath(pathname);
    const country = countryFromPath || LANDING_DEFAULT_COUNTRY;
    const campaignSlug = countryFromPath
      ? this.getCampaignSlugFromPath(pathname)
      : null;

    const canonicalPath = campaignSlug
      ? this.getCampaignCanonicalPath(country, campaignSlug)
      : this.getDefaultCanonicalPath(country);
    const isCanonicalPath = pathname === canonicalPath;

    if (campaignSlug) {
      return {
        kind: "campaign",
        country,
        campaignSlug,
        isCanonicalPath,
        canonicalPath,
      };
    }

    return {
      kind: "default",
      country,
      isCanonicalPath,
      canonicalPath,
    };
  }

  /**
   * Extracts country from `/landing/:country` URL path segment.
   *
   * @param pathname Current browser pathname.
   * @returns Normalized supported country code, or `null` if invalid/missing.
   */
  private getCountryFromPath(pathname: string): CountryCodeEnum | null {
    const pathParts = pathname.split("/");
    const country = pathParts[LANDING_COUNTRY_SEGMENT_INDEX];
    return this.normalizeCountryCode(country);
  }

  /**
   * Extracts and normalizes the campaign slug from `/landing/:country/:campaign`.
   *
   * @param pathname Current browser pathname.
   * @returns Lowercase campaign slug, or `null` when no campaign segment exists.
   */
  private getCampaignSlugFromPath(pathname: string): string | null {
    const pathParts = pathname.split("/");
    const campaignSlug = pathParts[LANDING_CAMPAIGN_SEGMENT_INDEX];
    return campaignSlug ? campaignSlug.toLowerCase() : null;
  }

  /**
   * Normalizes raw country input to uppercase enum format and validates support.
   *
   * @param country Raw country input from path segment.
   * @returns Supported `CountryCodeEnum` value, or `null` when unsupported.
   */
  private normalizeCountryCode(country: string | null): CountryCodeEnum | null {
    if (!country) {
      return null;
    }

    const normalizedCountry = country.toUpperCase();
    return this.isSupportedCountry(normalizedCountry)
      ? normalizedCountry
      : null;
  }

  /**
   * Checks whether the current path is part of the landing route space.
   *
   * @param pathname Current browser pathname.
   * @returns `true` for `/landing` and `/landing/*`; otherwise `false`.
   */
  private isLandingRoute(pathname: string): boolean {
    return (
      pathname === LANDING_ROUTE_PATH ||
      pathname.startsWith(`${LANDING_ROUTE_PATH}/`)
    );
  }

  /**
   * Builds canonical default landing path using lowercase country segment.
   *
   * @param country Supported country enum.
   * @returns Canonical path in `/landing/:country` format.
   */
  private getDefaultCanonicalPath(country: CountryCodeEnum): string {
    return `${LANDING_ROUTE_PATH}/${country.toLowerCase()}`;
  }

  /**
   * Builds canonical campaign landing path using lowercase country and slug.
   *
   * @param country Supported country enum.
   * @param campaignSlug Campaign slug.
   * @returns Canonical path in `/landing/:country/:campaign` format.
   */
  private getCampaignCanonicalPath(
    country: CountryCodeEnum,
    campaignSlug: string
  ): string {
    return `${this.getDefaultCanonicalPath(country)}/${campaignSlug.toLowerCase()}`;
  }

  /**
   * Returns current browser pathname from document location.
   *
   * @returns Current pathname or empty string when unavailable.
   */
  private getCurrentPathname(): string {
    return this.document.location.pathname || "";
  }

  /**
   * Validates whether a value matches one of the supported country enum values.
   *
   * @param country Candidate country value.
   * @returns `true` when country is supported; otherwise `false`.
   */
  private isSupportedCountry(country: unknown): country is CountryCodeEnum {
    return (
      typeof country === "string" &&
      SUPPORTED_COUNTRIES.includes(country as CountryCodeEnum)
    );
  }
}
